import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Clock, Loader2, Sparkles, Trophy } from 'lucide-react';

import { useLanguage } from 'shared/context/LanguageContext';
import { ltrIsolate } from 'shared/lib/bidi';
import { usePartnerAuth } from 'features/partner-auth';

import DashboardAccessCard from '../components/DashboardAccessCard';
import ProgressSummaryCard from '../components/ProgressSummaryCard';
import VerticalStepper from '../components/VerticalStepper';
import { usePortalUI } from '../context/PortalUIContext';
import { usePartnerStatus } from '../hooks/usePartnerStatus';

const PortalPage: React.FC = () => {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const isHebrew = language !== 'en';
  const { partner } = usePartnerAuth();

  const { status, isLoading, isFetching, error, refetch } = usePartnerStatus();
  const { setStickyTitle, setOnRefresh, setIsRefreshing } = usePortalUI();
  const [heroEl, setHeroEl] = useState<HTMLElement | null>(null);
  const businessName = partner?.commercialName ?? partner?.ownerName ?? '';

  useEffect(() => {
    setOnRefresh(refetch);
    return () => setOnRefresh(null);
  }, [refetch, setOnRefresh]);

  useEffect(() => {
    setIsRefreshing(isFetching);
  }, [isFetching, setIsRefreshing]);

  useEffect(() => {
    if (!heroEl || !businessName) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        // Hide as soon as the hero re-intersects the (margin-adjusted) viewport
        // on scroll-up. Show only once the hero is clearly past the header on
        // scroll-down, leaving a small dead-zone to avoid boundary flicker.
        if (entry.isIntersecting) {
          setStickyTitle(null);
          return;
        }
        if (entry.boundingClientRect.top < -24) {
          setStickyTitle(businessName);
        }
      },
      // rootMargin top = -64px matches the sticky header height so the
      // sticky title appears exactly when the greeting slides behind it.
      { threshold: 0, rootMargin: '-64px 0px 0px 0px' },
    );
    obs.observe(heroEl);
    return () => {
      obs.disconnect();
      setStickyTitle(null);
    };
  }, [heroEl, businessName, setStickyTitle]);

  if (isLoading) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center text-gray-500">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (error || !status) {
    return (
      <div className="rounded-2xl bg-red-50 border border-red-200 p-6 text-red-700">
        {t('partnerPortal.errors.loadStatus')}
      </div>
    );
  }

  const currentStage = status.webSteps.find((s) => s.status === 'current');
  const currentStageTitle = currentStage
    ? isHebrew
      ? currentStage.titleHe
      : currentStage.titleEn
    : '—';
  const completedCount = status.webSteps.filter(
    (s) => s.status === 'completed',
  ).length;

  return (
    <div className="space-y-6">
      {/* Hero - lighter, less red */}
      <motion.section
        ref={setHeroEl}
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative overflow-hidden rounded-3xl border border-gray-100 bg-white shadow-sm"
      >
        <div
          aria-hidden="true"
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(600px 280px at 100% 0%, rgba(192,0,53,0.06) 0%, transparent 65%)',
          }}
        />
        <div className="relative z-10 p-7 sm:p-9">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#C00035]/10 border border-[#C00035]/20 text-xs font-medium text-[#C00035] mb-4">
            <Sparkles className="h-3.5 w-3.5" />
            {t('partnerPortal.hero.tag')}
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold leading-tight text-gray-900">
            {t('partnerPortal.hero.greeting', {
              name: partner?.commercialName ?? partner?.ownerName ?? '',
            })}
          </h1>
          <p className="text-gray-500 mt-2">
            {t('partnerPortal.hero.subtitle', {
              business: partner?.commercialName ?? '',
            })}
          </p>
        </div>
      </motion.section>

      {/* Progress summary - one compact card instead of the tile grid on
          phones, so the onboarding steps stay reachable without scrolling. */}
      <motion.section
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.05 }}
        className="sm:hidden"
      >
        <ProgressSummaryCard
          currentStageTitle={currentStageTitle}
          completedCount={completedCount}
          totalCount={status.webSteps.length}
          progressPercent={status.progressPercent}
        />
      </motion.section>

      {/* KPI row - outside the hero */}
      <motion.section
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.05 }}
        className="hidden sm:grid sm:grid-cols-3 gap-3"
      >
        <KpiTile
          icon={<Clock className="h-5 w-5" />}
          label={t('partnerPortal.kpi.currentStage')}
          value={currentStageTitle}
          accentClass="text-[#C00035] bg-[#C00035]/10"
        />
        <KpiTile
          icon={<Sparkles className="h-5 w-5" />}
          label={t('partnerPortal.kpi.completed')}
          value={ltrIsolate(`${completedCount} / ${status.webSteps.length}`)}
          accentClass="text-emerald-600 bg-emerald-50"
        />
        <KpiTile
          icon={<Trophy className="h-5 w-5" />}
          label={t('partnerPortal.kpi.progress')}
          value={ltrIsolate(`${status.progressPercent}%`)}
          accentClass="text-[#C00035] bg-[#C00035]/10"
          extra={
            <div className="mt-3 h-1.5 rounded-full bg-gray-100 overflow-hidden">
              <motion.div
                className="h-full rounded-full bg-gradient-to-r from-[#C00035] to-[#99002A]"
                initial={{ width: 0 }}
                animate={{ width: `${status.progressPercent}%` }}
                transition={{ duration: 1, ease: 'easeOut' }}
              />
            </div>
          }
        />
      </motion.section>

      {/* Info banners */}
      {status.meta.pipedriveError && (
        <motion.section
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          className="rounded-2xl bg-amber-50 border border-amber-200 p-4 flex items-start gap-3 text-amber-900"
        >
          <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5" />
          <p className="text-sm leading-relaxed">
            {t('partnerPortal.stepper.liveError')}
          </p>
        </motion.section>
      )}

      {/* Horizontal stepper */}
      <motion.section
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.15 }}
        className="rounded-3xl bg-white border border-gray-100 p-6 sm:p-8 shadow-sm"
      >
        <div className="mb-6">
          <h2 className="text-lg font-bold text-gray-900">
            {t('partnerPortal.stepper.heading')}
          </h2>
          <p className="text-sm text-gray-500 mt-0.5">
            {t('partnerPortal.stepper.subheading')}
          </p>
        </div>
        <VerticalStepper steps={status.webSteps} />
      </motion.section>

      {status.hasDashboardAccess && <DashboardAccessCard />}
    </div>
  );
};

interface KpiTileProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  extra?: React.ReactNode;
  accentClass?: string;
}

const KpiTile: React.FC<KpiTileProps> = ({
  icon,
  label,
  value,
  extra,
  accentClass = 'text-gray-600 bg-gray-50',
}) => (
  <div className="rounded-2xl bg-white border border-gray-100 shadow-sm p-4">
    <div className="flex items-center gap-2">
      <span
        className={`inline-flex items-center justify-center h-8 w-8 rounded-lg ${accentClass}`}
      >
        {icon}
      </span>
      <span className="text-xs uppercase tracking-wide font-semibold text-gray-500">
        {label}
      </span>
    </div>
    <div className="text-2xl font-bold mt-2 text-gray-900">{value}</div>
    {extra}
  </div>
);

export default PortalPage;
